import {
  DefaultError,
  MutationCache,
  Query,
  QueryCache,
  QueryClient,
  useMutation as tanStackUseMutation,
  useQuery as tanStackUseQuery,
  type Mutation,
  type QueryKey,
  type UndefinedInitialDataOptions,
  type UseMutationOptions,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { ErrorModel } from 'app/src/model'
import { useWebAppContext } from 'app/src/shared/hooks'
import { WebAppContextType } from './context'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertReactQueryError<T>(error: any, propertyToFind: string[] = ['title', 'status']): T | undefined {
  return convertError(error?.body, propertyToFind)
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function convertError<T>(error: any, propertyToFind: string[] = ['title', 'status']): T | undefined {
  if (!error) return undefined
  else {
    return propertyToFind?.every((ai) => Object.keys(error).includes(ai)) ? error : undefined
  }
}

const retrieveContext = (query: Query<unknown, unknown, unknown>): WebAppContextType => {
  return query.options.meta?.['context'] as WebAppContextType
}

const retrieveContextMutation = (mutation: Mutation<unknown, unknown, unknown>): WebAppContextType => {
  return mutation.options.meta?.['context'] as WebAppContextType
}

const onStructuredError = (_error: ErrorModel, query: Query<unknown, unknown, unknown>) => {
  retrieveContext(query)?.showToastMessage({ content: 'Errore generico', severity: 'error' })
}

const onGenericError = (_error: DefaultError, query: Query<unknown, unknown, unknown>) => {
  const ctx = retrieveContext(query)

  if (_error && JSON.stringify(_error).includes('SESSION_EXPIRED')) {
    ctx?.update({ sessionExpired: true })
  } else if (_error && JSON.stringify(_error).includes('REGISTRATION_REQUIRED')) {
    ctx?.update({ userInfo: { ...ctx.userInfo, canSelfRegister: true } })
  } else {
    if (query?.meta?.hideToastErrorMessage) return
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    if (query?.meta?.skip404Errors && _error && (_error as any)?.payload) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const skip404 = typeof _error === 'object' && (_error as any)?.payload?.status === 404
      if (skip404) return
    }
    const queryToastErrorMessage = query?.meta?.toastErrorMessage
    retrieveContext(query)?.showToastMessage({
      content: queryToastErrorMessage && typeof queryToastErrorMessage === 'string' ? queryToastErrorMessage : 'Errore generico',
      severity: 'error',
    })
  }
}
export const onError = (error: DefaultError, query: Query<unknown, unknown, unknown>) => {
  const structuredEvent = convertReactQueryError<ErrorModel>(error)
  if (typeof query?.meta?.preventGlobalError === 'undefined' || !query?.meta?.preventGlobalError) {
    if (structuredEvent) onStructuredError(structuredEvent, query)
    else {
      onGenericError(error, query)
    }
  }
}

export const onErrorMutation = (
  error: Error,
  _variables: unknown,
  _context: unknown,
  mutation: Mutation<unknown, unknown, unknown>,
) => {
  const ctx = retrieveContextMutation(mutation)
  if (error && JSON.stringify(error).includes('SESSION_EXPIRED')) {
    ctx?.update({ sessionExpired: true })
  }

  if (error && JSON.stringify(error).includes('REGISTRATION_REQUIRED')) {
    ctx?.update({ userInfo: { ...ctx.userInfo, canSelfRegister: true } })
  }
  // else {
  //   ctx?.showToastMessage({ content: 'Errore generico', severity: 'error' })
  // }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError }),
  mutationCache: new MutationCache({ onError: onErrorMutation }),
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
      staleTime: (query) => {
        let staleTime = 0
        if (query?.queryKey?.some((x) => typeof x === 'string' && x === 'cacheable')) {
          if (query.queryKey.some((x) => typeof x === 'string' && x.startsWith('cacheable-'))) {
            const cacheableParam = query.queryKey.find((x) => typeof x === 'string' && x.startsWith('cacheable-'))
            const splitted = (cacheableParam as string).split('-')
            if (splitted.length === 2) staleTime = Number.parseInt(splitted[1])
          }
          if (staleTime === 0) staleTime = 60000
        }

        return staleTime
      },
    },
  },
})

export function resolveUrl(
  url: string,
  queryParams: Record<string, string | undefined> = {},
  pathParams: Record<string, string> = {},
): string {
  const qp = Object.entries(queryParams).reduce<Record<string, string>>((acc, [key, value]) => {
    if (value !== undefined) {
      acc[key] = value
    }
    return acc
  }, {})

  let query = new URLSearchParams(qp).toString()
  if (query) query = `?${query}`

  return url.replace(/\{\w*\}/gu, (key) => pathParams[key.slice(1, -1)]) + query
}

export const useQuery = <
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UndefinedInitialDataOptions<TQueryFnData, TError, TData, TQueryKey>,
  queryClient?: QueryClient,
): UseQueryResult<TData, TError> => {
  const context = useWebAppContext()
  if (!options.meta) {
    options.meta = {}
  }
  options.meta.context = context

  return tanStackUseQuery(options, queryClient)
}

export const useMutation = <TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
  options: UseMutationOptions<TData, TError, TVariables, TContext>,
  queryClient?: QueryClient,
): UseMutationResult<TData, TError, TVariables, TContext> => {
  const context = useWebAppContext()

  if (!options.meta) {
    options.meta = {}
  }
  options.meta.context = context
  return tanStackUseMutation(options, queryClient)
}

export { fetchHandleException, fetchParseResponse } from '@health/booking-sdk-react/services/bb-booking'
export type { UseMutationOptions, UseQueryOptions } from '@tanstack/react-query'
